// src/pages/AdminExperiencePage.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { 
  getExperiences, 
  createExperience, 
  updateExperience, 
  deleteExperience, 
  Experience 
} from '../api/experienceService';
import './AdminExperiencePage.css';

const AdminExperiencePage: React.FC = () => {
  const { user } = useAuth();
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [open, setOpen] = useState(false);
  const [currentExperience, setCurrentExperience] = useState<Partial<Experience>>({});
  const [isEditing, setIsEditing] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [experienceToDelete, setExperienceToDelete] = useState<number | null>(null);

  // Fetch experiences
  const fetchExperiences = async () => {
    try {
      setLoading(true);
      const data = await getExperiences();
      setExperiences(data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching experiences:', err);
      setError(`Failed to load experiences: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperiences();
  }, []);

  // Handle dialog open for create/edit
  const handleOpenDialog = (experience?: Experience) => {
    if (experience) {
      setCurrentExperience(experience);
      setIsEditing(true);
    } else {
      setCurrentExperience({
        title: '',
        company: '',
        location: '',
        date_start: '',
        date_end: '',
        description: '',
        achievements: '',
        is_current: false
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentExperience({});
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCurrentExperience({
      ...currentExperience,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (isEditing && currentExperience.id) {
        await updateExperience(currentExperience.id, currentExperience);
        setSuccess('Experience updated successfully');
      } else {
        await createExperience(currentExperience as Omit<Experience, 'id'>);
        setSuccess('Experience created successfully');
      }
      handleCloseDialog();
      fetchExperiences();
    } catch (err: any) {
      setError(`Failed to save experience: ${err.message}`);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    setExperienceToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (experienceToDelete) {
      try {
        await deleteExperience(experienceToDelete);
        setSuccess('Experience deleted successfully');
        fetchExperiences();
      } catch (err: any) {
        setError(`Failed to delete experience: ${err.message}`);
      }
    }
    setDeleteDialogOpen(false);
    setExperienceToDelete(null);
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Check if user can edit (admin or editor)
  const canEdit = user && ['admin', 'editor'].includes(user.role);
  
  // Check if user can delete (admin only)
  const canDelete = user && user.role === 'admin';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Experiences
          </Typography>
          
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Experience
            </Button>
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experiences.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell>{exp.title}</TableCell>
                    <TableCell>{exp.company}</TableCell>
                    <TableCell>
                      {exp.is_current
                        ? `${exp.date_start} - Present`
                        : `${exp.date_start} - ${exp.date_end || ''}`}
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <IconButton onClick={() => handleOpenDialog(exp)} color="primary">
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton onClick={() => handleDelete(exp.id!)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {experiences.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No experiences found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Experience' : 'Add Experience'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            label="Title"
            name="title"
            fullWidth
            value={currentExperience.title || ''}
            onChange={handleInputChange}
          />
          <TextField
            margin="normal"
            label="Company"
            name="company"
            fullWidth
            value={currentExperience.company || ''}
            onChange={handleInputChange}
          />
          <TextField
            margin="normal"
            label="Location"
            name="location"
            fullWidth
            value={currentExperience.location || ''}
            onChange={handleInputChange}
          />
          <TextField
            margin="normal"
            label="Start Date"
            name="date_start"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={currentExperience.date_start || ''}
            onChange={handleInputChange}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentExperience.is_current || false}
                onChange={handleInputChange}
                name="is_current"
              />
            }
            label="Current Position"
          />
          {!currentExperience.is_current && (
            <TextField
              margin="normal"
              label="End Date"
              name="date_end"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={currentExperience.date_end || ''}
              onChange={handleInputChange}
            />
          )}
          <TextField
            margin="normal"
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={4}
            value={currentExperience.description || ''}
            onChange={handleInputChange}
          />
          <TextField
            margin="normal"
            label="Achievements"
            name="achievements"
            fullWidth
            multiline
            rows={4}
            value={currentExperience.achievements || ''}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this experience? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminExperiencePage;
